import { EVENTS, MESSAGE_SOURCE, MESSAGE_TYPES } from '../shared/constants';
import { EventEmitter } from '../shared/event-emitter';
import type {
  MessageEnvelope,
  WidgetEventMessagePayload,
  WidgetEventPayload,
  WidgetEventPayloadMap,
  WidgetEventName,
  WidgetInitMessage,
  WidgetOptions,
  WidgetErrorPayload,
} from '../shared/types';
import {
  buildIframeSrc,
  createHandshakeId,
  getOrigin,
  isMessageEnvelope,
  normalizeOptions,
  resolveElement,
} from '../shared/utils';

const DEFAULT_IFRAME_STYLE: Partial<CSSStyleDeclaration> = {
  border: '0',
  width: '100%',
  height: '100%',
  display: 'block',
};

export type ReadyCallback = (widget: AIFindrCommerceWidget) => void;

type ReadyResolver = (widget: AIFindrCommerceWidget) => void;
type ReadyRejector = (error: unknown) => void;

export class AIFindrCommerceWidget {
  public static readonly EVENTS = EVENTS;

  private readonly options: WidgetOptions;
  private readonly container: HTMLElement;
  private readonly iframe: HTMLIFrameElement;
  private readonly eventEmitter = new EventEmitter<WidgetEventName, WidgetEventPayloadMap>();
  private readonly readyPromise: Promise<AIFindrCommerceWidget>;
  private readonly handshakeId = createHandshakeId();
  private readonly targetOrigin: string;

  private readyResolver!: ReadyResolver;
  private readyRejector!: ReadyRejector;
  private readyState = false;
  private destroyed = false;
  private handshakeTimeout?: ReturnType<typeof setTimeout>;

  private readonly boundMessageHandler = (event: MessageEvent) => this.handleMessage(event);

  constructor(options: WidgetOptions) {
    this.options = normalizeOptions(options);
    this.validateOptions();

    this.container = resolveElement(this.options.renderTo);
    this.targetOrigin = getOrigin(this.options.baseUrl);

    this.readyPromise = new Promise<AIFindrCommerceWidget>((resolve, reject) => {
      this.readyResolver = resolve;
      this.readyRejector = reject;
    });

    this.iframe = this.createIframe();
    this.container.appendChild(this.iframe);

    window.addEventListener('message', this.boundMessageHandler);

    // Set a timeout for handshake - only try once
    this.handshakeTimeout = setTimeout(() => {
      if (!this.readyState && !this.destroyed) {
        console.warn('AIFindrCommerceWidget: Unable to establish connection with iframe. The iframe may not have the bridge library installed.');
        this.handleHandshakeFailure();
      }
    }, 10000); // 10 second timeout
  }

  public ready(): Promise<AIFindrCommerceWidget>;
  public ready(callback: ReadyCallback): AIFindrCommerceWidget;
  public ready(callback?: ReadyCallback): Promise<AIFindrCommerceWidget> | AIFindrCommerceWidget {
    if (typeof callback === 'function') {
      void this.readyPromise.then(() => callback(this));
      return this;
    }
    return this.readyPromise;
  }

  public isReady(): boolean {
    return this.readyState;
  }

  public on<TEvent extends WidgetEventName>(event: TEvent, listener: (payload: WidgetEventPayload<TEvent>) => void): this {
    this.eventEmitter.on(event, listener);
    return this;
  }

  public off<TEvent extends WidgetEventName>(event: TEvent, listener: (payload: WidgetEventPayload<TEvent>) => void): this {
    this.eventEmitter.off(event, listener);
    return this;
  }

  public destroy(): void {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;

    if (this.handshakeTimeout) {
      clearTimeout(this.handshakeTimeout);
      this.handshakeTimeout = undefined;
    }

    window.removeEventListener('message', this.boundMessageHandler);
    this.eventEmitter.removeAllListeners();
    if (!this.readyState) {
      this.readyRejector?.(new Error('Widget destroyed before it became ready.'));
    }
    if (this.iframe.parentElement === this.container) {
      this.container.removeChild(this.iframe);
    }
  }

  private validateOptions(): void {
    if (!this.options) {
      throw new Error('AIFindrCommerceWidget: options are required.');
    }

    if (!this.options.clientId || typeof this.options.clientId !== 'string') {
      throw new Error('AIFindrCommerceWidget: clientId must be a non-empty string.');
    }

    if (!this.options.baseUrl || typeof this.options.baseUrl !== 'string') {
      throw new Error('AIFindrCommerceWidget: baseUrl must be a non-empty string URL.');
    }

    if (!this.options.renderTo) {
      throw new Error('AIFindrCommerceWidget: renderTo option is required.');
    }
  }

  private createIframe(): HTMLIFrameElement {
    const iframe = document.createElement('iframe');
    iframe.src = buildIframeSrc(this.options.baseUrl, this.options.clientId, this.handshakeId);
    iframe.title = this.options.iframeTitle ?? 'AIFindr Commerce Widget';
    iframe.setAttribute('data-aifindr-widget', 'true');

    Object.assign(iframe.style, DEFAULT_IFRAME_STYLE);

    const customAttrs = this.options.iframeAttributes ?? {};
    if (customAttrs.allow) {
      iframe.setAttribute('allow', customAttrs.allow);
    }
    if (customAttrs.referrerPolicy) {
      iframe.referrerPolicy = customAttrs.referrerPolicy;
    }
    if (customAttrs.sandbox) {
      iframe.setAttribute('sandbox', customAttrs.sandbox);
    }

    iframe.addEventListener('load', () => {
      const initPayload: WidgetInitMessage = {
        clientId: this.options.clientId,
        context: this.options.context,
      };
      this.postMessage(MESSAGE_TYPES.INIT, initPayload);
    });

    return iframe;
  }

  private handleMessage(event: MessageEvent): void {
    if (!this.iframe || event.source !== this.iframe.contentWindow) {
      return;
    }

    if (event.origin !== this.targetOrigin) {
      return;
    }

    if (!isMessageEnvelope(event.data)) {
      return;
    }

    const envelope = event.data;
    if (envelope.source !== MESSAGE_SOURCE || envelope.handshake !== this.handshakeId) {
      return;
    }

    switch (envelope.type) {
      case MESSAGE_TYPES.READY:
        this.handleReady();
        break;
      case MESSAGE_TYPES.EVENT:
        this.handleEvent(envelope.payload as WidgetEventMessagePayload);
        break;
      case MESSAGE_TYPES.ERROR:
        this.handleError(envelope.payload as WidgetErrorPayload);
        break;
      default:
        break;
    }
  }

  private handleReady(): void {
    if (this.readyState) {
      return;
    }

    // Clear handshake timeout since we successfully connected
    if (this.handshakeTimeout) {
      clearTimeout(this.handshakeTimeout);
      this.handshakeTimeout = undefined;
    }

    this.readyState = true;
    this.eventEmitter.emit(EVENTS.WIDGET_READY, undefined);
    this.readyResolver(this);
  }

  private handleHandshakeFailure(): void {
    // Iframe loaded but didn't respond to handshake
    // Still resolve the promise so the widget can be used, but it won't have two-way communication
    this.readyState = false; // Keep as false since handshake failed
    this.readyResolver(this); // Resolve anyway to unblock code waiting on ready()
  }

  private handleEvent(payload?: WidgetEventMessagePayload): void {
    if (!payload) {
      return;
    }

    const { event, data } = payload;
    this.eventEmitter.emit(event as WidgetEventName, data as WidgetEventPayload<WidgetEventName>);
  }

  private handleError(payload?: WidgetErrorPayload): void {
    this.eventEmitter.emit(EVENTS.WIDGET_ERROR, payload ?? { message: 'Unknown widget error' });
  }

  private postMessage<TPayload>(type: string, payload?: TPayload): void {
    if (!this.iframe.contentWindow) {
      return;
    }

    const envelope: MessageEnvelope<typeof type, TPayload> = {
      source: MESSAGE_SOURCE,
      type,
      handshake: this.handshakeId,
      payload,
    };

    this.iframe.contentWindow.postMessage(envelope, this.targetOrigin);
  }
}

export type { WidgetOptions, WidgetEventName, WidgetEventPayload };
export default AIFindrCommerceWidget;
